import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Bookmark, Loader2, Maximize2, Play, Search, X, Youtube } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import { useNewsArticles, useNewsSources, useYouTubeSearch, useYouTubeHistory, useRecordWatch } from '../../hooks/useNews'
import { useMiniPlayer } from '../../contexts/MiniPlayerContext'
import { supabase } from '../../lib/supabaseClient'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'

// Compact "X hours ago" for the channel-name row below the title.
// Mirrors the pattern in NewsPage.timeAgo but localised inline so we
// can keep the labels short ("2س", "2h").
function timeAgo(iso, locale) {
  if (!iso) return ''
  const then = Date.parse(iso)
  if (!Number.isFinite(then)) return ''
  const diff = Date.now() - then
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return locale === 'ar' ? 'الآن' : 'now'
  if (mins < 60) return locale === 'ar' ? `${mins}د` : `${mins}m`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return locale === 'ar' ? `${hours}س` : `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return locale === 'ar' ? `${days}ي` : `${days}d`
  return new Date(then).toLocaleDateString(locale === 'ar' ? 'ar-EG' : 'en-CA')
}

// Format seconds as MM:SS or H:MM:SS. Stays compact so it fits in the
// bottom-right thumbnail badge without wrapping.
function formatDuration(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return null
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  const pad = (n) => String(n).padStart(2, '0')
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`
  return `${m}:${pad(s)}`
}

// 1.2K / 4.5M-style compact view count. YouTube uses two significant
// digits below 10K, one decimal above. We follow the same convention
// so the number stays legible at the metadata-row font size.
function formatCount(n) {
  if (!Number.isFinite(n) || n < 0) return null
  if (n < 1000) return String(n)
  if (n < 10000) {
    const k = n / 1000
    return `${k.toFixed(1).replace(/\.0$/, '')}K`
  }
  if (n < 1000000) return `${Math.round(n / 1000)}K`
  if (n < 10000000) {
    const m = n / 1000000
    return `${m.toFixed(1).replace(/\.0$/, '')}M`
  }
  if (n < 1000000000) return `${Math.round(n / 1000000)}M`
  const b = n / 1000000000
  return `${b.toFixed(1).replace(/\.0$/, '')}B`
}

// Pull the video id from a YouTube URL. Mirrors NewsArticlePage's
// extractor; duplicated here so this page doesn't have to import
// from a sibling page file.
function extractYouTubeId(url) {
  if (!url) return null
  try {
    const u = new URL(url)
    if (/(^|\.)youtu\.be$/i.test(u.hostname)) {
      return u.pathname.replace(/^\//, '') || null
    }
    if (/(^|\.)youtube\.com$/i.test(u.hostname)) {
      const v = u.searchParams.get('v')
      if (v) return v
      const m = u.pathname.match(/^\/(?:shorts|embed)\/([^/?]+)/)
      if (m) return m[1]
    }
  } catch { /* fall through */ }
  return null
}

// One video card. The default state is a clickable 16:9 thumbnail
// with a centred play button overlay. Tapping flips local state and
// swaps the thumbnail with the youtube-nocookie embed — no
// navigation, the user stays in the list and can scroll past to the
// next one. Closing isn't necessary: scrolling away unmounts via the
// virtual list bounds (currently no virtualisation, but the iframe
// is small enough that leaving it mounted is fine).
const VideoCard = React.memo(function VideoCard({ article, locale, channelName, channelAvatar, t, onWatch }) {
  const [playing, setPlaying] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)
  const videoId = useMemo(() => extractYouTubeId(article.link), [article.link])
  const { openMini } = useMiniPlayer()
  const showImage = !!article.image_url && !imgFailed
  const duration = formatDuration(article.media_duration_seconds)
  const viewsCompact = formatCount(article.media_view_count)
  const isLive = !!article.media_is_live
  const isShort = !!article.media_is_short
  const isExternal = !!article.external

  // Save state for external (search-result) videos. The cached
  // archive cards aren't savable from here — they already live in
  // news_articles, the regular news-page bookmark covers them.
  // Local state only; we don't surface saved videos elsewhere from
  // this page since the saved view in /phoenix/news?view=saved is
  // the canonical home for them.
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const handleSave = async (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (saving || saved) return
    setSaving(true)
    setSaveError(null)
    try {
      const { data, error } = await supabase.functions.invoke('save-external-video', {
        body: { video: article },
      })
      if (error) throw error
      if (data?.saved) setSaved(true)
      else setSaveError(data?.error || 'unknown')
    } catch (err) {
      setSaveError(err?.message || 'failed')
    } finally {
      setSaving(false)
    }
  }

  const handlePlay = async () => {
    if (!videoId) return
    // Cached videos: fire-and-forget upsert into youtube_watch_history.
    if (!article.external && article.id && onWatch) {
      onWatch(article.id)
    }
    // External (search-result) videos: they don't have a news_articles
    // row yet, so we can't record a watch without one. Call
    // save-external-video with bookmark:false to materialise the row
    // (lands under the youtube_external bucket or a matched channel),
    // get the article_id back, then record the watch. Failure paths
    // shouldn't block playback — we start the iframe regardless.
    if (article.external && onWatch) {
      supabase.functions
        .invoke('save-external-video', {
          body: { video: article, bookmark: false },
        })
        .then(({ data, error }) => {
          if (!error && data?.article_id) onWatch(data.article_id)
        })
    }
    setPlaying(true)
  }

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        {/* Player / thumbnail. aspect-video keeps the 16:9 frame
            even when the user taps to swap in the iframe — no layout
            shift. */}
        <div className="relative aspect-video w-full bg-muted">
          {playing && videoId ? (
            <>
              <iframe
                title={article.title}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
              {/* Pop-out into the floating mini player. Tapping
                  this swaps the inline iframe for a global
                  floating window the user can keep watching while
                  they navigate elsewhere in the app. */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  openMini({
                    kind: 'youtube',
                    id: article.id || videoId,
                    src: videoId,
                    title: article.title,
                  })
                  setPlaying(false)
                }}
                aria-label={t('miniPlayer.popOut')}
                title={t('miniPlayer.popOut')}
                className="absolute top-1.5 end-1.5 w-8 h-8 rounded-full bg-black/70 text-white inline-flex items-center justify-center hover:bg-black/85 z-10"
              >
                <Maximize2 className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={handlePlay}
              className="absolute inset-0 group block"
              aria-label={article.title}
              disabled={!videoId}
            >
              {showImage && (
                <img
                  src={(article.image_url || '').replace(/&amp;/g, '&')}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                  onError={() => setImgFailed(true)}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              )}
              {/* Subtle dark gradient so the play icon stays legible
                  over busy thumbnails. */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              {/* Top-start corner: short/live badges. Stacked vertically
                  when both could ever co-occur (they can't right now —
                  live ≠ short — but the layout still tolerates it). */}
              <div className="absolute top-1.5 start-1.5 flex flex-col gap-1 pointer-events-none">
                {isLive && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-red-600 inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    {t('news.youtube.live')}
                  </span>
                )}
                {isShort && !isLive && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white bg-black/70">
                    Shorts
                  </span>
                )}
                {isExternal && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white bg-black/70">
                    {t('news.youtube.externalBadge')}
                  </span>
                )}
              </div>
              {/* Duration badge bottom-end. Hidden for live streams
                  (they don't have a fixed duration). */}
              {duration && !isLive && (
                <span className="absolute bottom-1.5 end-1.5 px-1.5 py-0.5 rounded text-[11px] font-mono font-semibold text-white bg-black/80" dir="ltr">
                  {duration}
                </span>
              )}
              {/* Centre play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="w-14 h-14 rounded-full bg-red-600/90 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:bg-red-600 group-active:scale-95 transition-all">
                  <Play className="w-7 h-7 text-white fill-white ms-1" />
                </span>
              </div>
            </button>
          )}
        </div>

        {/* Title + meta strip below the player */}
        <div className="p-3 flex gap-2.5">
          {channelAvatar && (
            <img
              src={channelAvatar}
              alt=""
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
              className="w-9 h-9 rounded-full flex-shrink-0 object-cover bg-muted"
            />
          )}
          <div className="min-w-0 flex-1">
            <p
              className="text-sm font-semibold text-foreground line-clamp-2 leading-snug"
              dir="auto"
            >
              {article.title}
            </p>
            <div
              className="flex items-center gap-1.5 mt-1 text-[11px] text-muted-foreground flex-wrap"
              dir="auto"
            >
              <span className="font-medium truncate max-w-[55%]">{channelName}</span>
              {viewsCompact && (
                <>
                  <span>·</span>
                  <span className="font-mono" dir="ltr">
                    {t('news.youtube.views', { count: viewsCompact })}
                  </span>
                </>
              )}
              <span>·</span>
              <span className="font-mono" dir="ltr">
                {timeAgo(article.published_at, locale)}
              </span>
            </div>
            {saveError && (
              <p className="text-[10px] text-red-600 mt-1" dir="auto">{saveError}</p>
            )}
          </div>
          {/* Save bookmark — only on external (live search) results.
              Cached cards already live in news_articles, the news-
              page bookmark covers them. Tap fires save-external-video
              which upserts the row + flags it saved for the user. */}
          {isExternal && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || saved}
              aria-pressed={saved}
              aria-label={t(saved ? 'news.youtube.saved' : 'news.youtube.save')}
              title={t(saved ? 'news.youtube.saved' : 'news.youtube.save')}
              className={cn(
                'flex-shrink-0 w-8 h-8 rounded-md inline-flex items-center justify-center transition-colors',
                saved
                  ? 'text-app-primary-500 bg-app-primary-500/10'
                  : 'text-muted-foreground hover:text-app-primary-500 hover:bg-accent',
                saving && 'opacity-60',
              )}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Bookmark className={cn('w-4 h-4', saved && 'fill-current')} />
              )}
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  )
})

// Channel chip in the horizontal filter row. Avatar shows on the
// leading edge when the channel sync has populated it; falls back to
// the plain text chip otherwise.
function ChannelChip({ active, label, avatar, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-shrink-0 h-9 rounded-full text-xs font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1.5',
        avatar ? 'ps-1 pe-3' : 'px-3',
        active
          ? 'bg-app-primary-500 text-white'
          : 'bg-muted text-muted-foreground hover:bg-accent',
      )}
      dir="auto"
    >
      {avatar && (
        <img
          src={avatar}
          alt=""
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className="w-7 h-7 rounded-full object-cover flex-shrink-0 bg-background"
        />
      )}
      {label}
    </button>
  )
}

// Top-level tab pill (All / Videos / Shorts). Active state mirrors
// the news page's view tabs so the user gets a consistent control.
// Watch history list — appears when the History tab is active.
// Same VideoCard as the other surfaces; adds a Clear All button when
// there's any history, and an empty state when there isn't.
function HistorySection({ historyHook, locale, recordWatch, t }) {
  if (historyHook.loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="aspect-video w-full" />
        <Skeleton className="aspect-video w-full" />
        <Skeleton className="aspect-video w-full" />
      </div>
    )
  }
  if (historyHook.articles.length === 0) {
    return (
      <EmptyState
        icon={Youtube}
        title={t('news.youtube.historyEmpty')}
        description={t('news.youtube.historyEmptyHint')}
      />
    )
  }
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground" dir="auto">
          {t('news.youtube.historyCount', { count: historyHook.articles.length })}
        </p>
        <button
          type="button"
          onClick={() => {
            if (window.confirm(t('news.youtube.clearHistoryConfirm'))) {
              historyHook.clear()
            }
          }}
          className="text-[11px] text-muted-foreground hover:text-red-500 inline-flex items-center gap-1"
          dir="auto"
        >
          <X className="w-3 h-3" />
          {t('news.youtube.clearHistory')}
        </button>
      </div>
      <div className="space-y-3">
        {historyHook.articles.map((a) => (
          <VideoCard
            key={a.id}
            article={a}
            locale={locale}
            channelName={a.source?.name || ''}
            channelAvatar={a.source?.avatar_url || null}
            t={t}
            onWatch={recordWatch}
          />
        ))}
      </div>
    </>
  )
}

function TypeTab({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 h-9 rounded-full text-xs font-semibold transition-colors whitespace-nowrap px-3',
        active
          ? 'bg-app-primary-500 text-white shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
      dir="auto"
    >
      {label}
    </button>
  )
}

export function NewsYouTubePage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const locale = i18n.language === 'ar' ? 'ar' : 'en'

  const sourcesHook = useNewsSources()
  // Selected channel id — null = all channels.
  const [channelId, setChannelId] = useState(null)
  // Type filter — 'all' | 'videos' | 'shorts' | 'saved'. The first
  // three map to the media_is_short column on news_articles;
  // 'saved' switches the underlying view to the user's bookmarked
  // articles (still scoped to sourceKind=youtube so the saved tab
  // here only shows saved YouTube videos, not all bookmarked news).
  const [mediaType, setMediaType] = useState('all')
  // Search state. The draft updates immediately on keystroke; the
  // committed query (passed to the hook) follows on a 300ms
  // debounce so each character doesn't fire a Postgres FTS round-
  // trip. Same pattern as the news page's search box.
  const [searchDraft, setSearchDraft] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    if (searchDraft === search) return undefined
    const timer = setTimeout(() => setSearch(searchDraft.trim()), 300)
    return () => clearTimeout(timer)
  }, [searchDraft, search])

  const channels = useMemo(
    () => sourcesHook.sources
      .filter((s) => s.kind === 'youtube')
      .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [sourcesHook.sources],
  )

  // 'saved' tab flips the underlying news_articles view from the
  // default publish-time feed to the user's bookmarks. We also
  // skip the media_is_short filter in this mode so a saved Short
  // and a saved long-form video appear in the same list — the user
  // typically wants to find what they bookmarked, not filter it
  // further by media type.
  const articlesHook = useNewsArticles({
    sourceKind: 'youtube',
    sourceId: channelId,
    search: search || null,
    view: mediaType === 'saved' ? 'saved' : 'all',
    mediaType: mediaType === 'all' || mediaType === 'saved' || mediaType === 'history' ? undefined : mediaType,
  })

  // Watch history. Loaded eagerly so switching to the History tab is
  // instant; the hook caches the list and exposes a clear() helper.
  const historyHook = useYouTubeHistory()
  const recordWatch = useRecordWatch()
  const isHistory = mediaType === 'history'

  // External YouTube search (search.list, 100 quota units per call).
  // Stays inert until the user taps the explicit button — we don't
  // want to burn the daily budget on every keystroke. Resets when the
  // user clears the search box.
  const externalHook = useYouTubeSearch()
  const externalReset = externalHook.reset
  const externalQuery = externalHook.query
  useEffect(() => {
    // Clear external results when the user clears or changes the
    // committed search query — keeps the page from showing stale
    // YouTube hits next to a different local search.
    if (!search && externalQuery) externalReset()
  }, [search, externalQuery, externalReset])
  const runExternalSearch = useCallback(() => {
    if (!search) return
    externalHook.search(search, { language: i18n.language === 'ar' ? 'ar' : 'en' })
  }, [search, externalHook, i18n.language])

  const handleRefresh = useCallback(
    () => performRefresh([sourcesHook.refetch, articlesHook.refetch]),
    [sourcesHook.refetch, articlesHook.refetch],
  )

  // Infinite-scroll sentinel — same pattern as the main news page.
  const sentinelRef = useRef(null)
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined
    if (!articlesHook.hasMore || articlesHook.loading) return undefined
    const el = sentinelRef.current
    if (!el) return undefined
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) articlesHook.loadMore?.()
        }
      },
      { root: null, rootMargin: '800px 0px', threshold: 0 },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [articlesHook.hasMore, articlesHook.loading, articlesHook.loadMore, articlesHook.articles.length])

  // YouTube is its own standalone page now (not nested under news);
  // back goes to the phoenix dashboard which is where the user came
  // from via the Profile drawer.
  const goBack = () => navigate('/')

  const localArticles = articlesHook.articles
  const externalVideos = externalHook.videos
  const showExternal = externalVideos.length > 0

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="space-y-3 content-fade pb-8">
        <div className="flex items-center gap-2 mb-2">
          <button
            type="button"
            onClick={goBack}
            aria-label={t('settings.back')}
            className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground"
          >
            <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
          </button>
          <h1 className="text-xl font-bold text-foreground flex items-center gap-2" dir="auto">
            <Youtube className="w-5 h-5 text-red-500" />
            {t('news.youtube.title')}
          </h1>
        </div>

        {/* Search input. Runs against the title + summary tsvector
            on news_articles via the existing useNewsArticles search
            param — same FTS the news page already uses, so phrase
            queries and OR work. Scope is the YouTube cache only
            (sourceKind filter handles that); the "Search on YouTube"
            button below reaches out to YouTube's own search.list. */}
        <div className="relative">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder={t('news.youtube.searchPlaceholder')}
            aria-label={t('news.youtube.searchPlaceholder')}
            className="h-10 w-full ps-9 pe-9 rounded-lg border border-input bg-background text-foreground text-sm"
          />
          {searchDraft && (
            <button
              type="button"
              onClick={() => { setSearchDraft(''); setSearch('') }}
              aria-label={t('news.youtube.clearSearch')}
              className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Type-of-content tabs. Sit just below the search box so the
            user can scope to videos / shorts without losing the
            current query or channel filter. */}
        <div className="flex gap-1 p-1 bg-muted/60 rounded-full overflow-x-auto">
          <TypeTab active={mediaType === 'all'} label={t('news.youtube.tabAll')} onClick={() => setMediaType('all')} />
          <TypeTab active={mediaType === 'videos'} label={t('news.youtube.tabVideos')} onClick={() => setMediaType('videos')} />
          <TypeTab active={mediaType === 'shorts'} label={t('news.youtube.tabShorts')} onClick={() => setMediaType('shorts')} />
          <TypeTab active={mediaType === 'saved'} label={t('news.youtube.tabSaved')} onClick={() => setMediaType('saved')} />
          <TypeTab active={mediaType === 'history'} label={t('news.youtube.tabHistory')} onClick={() => setMediaType('history')} />
        </div>

        {/* Channel filter chips. The "All" chip is sticky to the
            start of the row so the user can always reset without
            scrolling back to the head. */}
        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          <ChannelChip
            active={channelId === null}
            label={t('news.youtube.allChannels')}
            onClick={() => setChannelId(null)}
          />
          {channels.map((c) => (
            <ChannelChip
              key={c.id}
              active={channelId === c.id}
              label={c.name}
              avatar={c.avatar_url}
              onClick={() => setChannelId(c.id)}
            />
          ))}
        </div>

        {isHistory ? (
          <HistorySection
            historyHook={historyHook}
            locale={locale}
            recordWatch={recordWatch}
            t={t}
          />
        ) : articlesHook.loading ? (
          <div className="space-y-3">
            <Skeleton className="aspect-video w-full" />
            <Skeleton className="aspect-video w-full" />
            <Skeleton className="aspect-video w-full" />
          </div>
        ) : articlesHook.error ? (
          <Card>
            <CardContent className="p-3 text-xs text-red-600" dir="auto">
              {articlesHook.error}
            </CardContent>
          </Card>
        ) : localArticles.length === 0 ? (
          <EmptyState
            icon={search ? Search : Youtube}
            title={search ? t('news.youtube.searchEmpty', { query: search }) : t('news.youtube.empty')}
            description={search ? t('news.youtube.searchEmptyHint') : t('news.youtube.emptyHint')}
          />
        ) : (
          <>
            <div className="space-y-3">
              {localArticles.map((a) => (
                <VideoCard
                  key={a.id}
                  article={a}
                  locale={locale}
                  channelName={a.source?.name || ''}
                  channelAvatar={a.source?.avatar_url || null}
                  t={t}
                  onWatch={recordWatch}
                />
              ))}
            </div>
            {articlesHook.hasMore && (
              <>
                <div ref={sentinelRef} aria-hidden="true" className="h-1 w-full" />
                <div className="pt-2 flex justify-center">
                  <button
                    type="button"
                    onClick={articlesHook.loadMore}
                    disabled={articlesHook.loadingMore}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-input text-muted-foreground hover:bg-accent disabled:opacity-50"
                  >
                    {articlesHook.loadingMore && (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    )}
                    {t('news.loadMore')}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* External "Search on YouTube" affordance. Only shows when
            the user has a committed query — search.list is the
            most expensive YouTube endpoint (100 quota units per call)
            so we don't want to fire it on an empty query, and we
            keep it opt-in (button tap) rather than auto. */}
        {search && (
          <div className="pt-2 space-y-2">
            {!showExternal && (
              <button
                type="button"
                onClick={runExternalSearch}
                disabled={externalHook.loading}
                className="w-full h-10 inline-flex items-center justify-center gap-1.5 rounded-lg border border-input bg-background hover:bg-accent text-foreground text-xs font-semibold disabled:opacity-60"
                dir="auto"
              >
                {externalHook.loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t('news.youtube.searchingYouTube')}
                  </>
                ) : (
                  <>
                    <Youtube className="w-4 h-4 text-red-500" />
                    {t('news.youtube.searchOnYouTube')}
                  </>
                )}
              </button>
            )}
            {externalHook.error && (
              <Card>
                <CardContent className="p-3 text-xs text-red-600" dir="auto">
                  {t('news.youtube.searchError', { error: externalHook.error })}
                </CardContent>
              </Card>
            )}
            {showExternal && (
              <>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <h2 className="text-xs font-semibold text-muted-foreground inline-flex items-center gap-1.5" dir="auto">
                    <Youtube className="w-3.5 h-3.5 text-red-500" />
                    {t('news.youtube.externalResults')}
                  </h2>
                  <button
                    type="button"
                    onClick={externalHook.reset}
                    className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    dir="auto"
                  >
                    <X className="w-3 h-3" />
                    {t('news.youtube.clearExternal')}
                  </button>
                </div>
                <div className="space-y-3">
                  {externalVideos.map((v) => (
                    <VideoCard
                      key={v.api_id || v.link}
                      article={{ ...v, id: v.api_id || v.link }}
                      locale={locale}
                      channelName={v.channel_name || ''}
                      channelAvatar={null}
                      t={t}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </PullToRefresh>
  )
}
